'use client'

import type { ReactNode } from 'react'
import { useEffect, useMemo } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { ExternalLink } from 'lucide-react'
import { z } from 'zod/v4'

import type { AvatarList } from '@helper/utils/domain'
import type { Locale } from '@helper/utils/types'
import { UpdateUserSchema } from '@package/drizzle/schema'
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldSet,
  Input,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/web/components'
import { AvatarSelector, ProfileUpload, typographyVariants } from '@ui/web/lib'
import { tryCatch } from '@helper/utils/core'
import { buildUrl } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

const GENDER_OPTIONS = (locale: Locale) =>
  [
    { label: locale === 'id' ? 'Laki-laki' : 'Male', value: 'MALE' },
    { label: locale === 'id' ? 'Perempuan' : 'Female', value: 'FEMALE' },
  ] as const

const DEFAULT_JOB_TITLE_OPTIONS = (locale: Locale) =>
  [
    { label: locale === 'id' ? 'Guru' : 'Teacher', value: 'Guru' },
    { label: locale === 'id' ? 'Kepala Sekolah' : 'School Principal', value: 'Kepala Sekolah' },
    { label: locale === 'id' ? 'Staf' : 'Staff', value: 'Staf' },
    { label: locale === 'id' ? 'Lainnya' : 'Other', value: 'Lainnya' },
  ] as const

/** How many years back the "Tahun lahir" select goes from the current year. */
const BIRTH_YEAR_RANGE = 100

function buildBirthYearOptions(currentYear: number) {
  return Array.from({ length: BIRTH_YEAR_RANGE + 1 }, (_, index) => {
    const year = String(currentYear - index)
    return { label: year, value: year }
  })
}

const UserDetailsFormSchema = (locale: Locale) =>
  UpdateUserSchema.pick({
    name: true,
    phoneNumber: true,
    nickname: true,
    gender: true,
    birthDate: true,
    image: true,
  })
    .extend({
      imageFile: z.instanceof(File).nullable(),
      jobTitle: z.string().nullable(),
    })
    .superRefine((data, ctx) => {
      if (!data.name) {
        ctx.addIssue({
          code: 'custom',
          message: locale === 'id' ? 'Nama harus diisi' : 'Name is required',
          path: ['name'],
        })
      }

      if (data.phoneNumber != null && data.phoneNumber !== '' && !/^\+?[0-9]+$/.test(data.phoneNumber)) {
        ctx.addIssue({
          code: 'custom',
          message: locale === 'id' ? 'Nomor telepon tidak valid' : 'Phone number is invalid',
          path: ['phoneNumber'],
        })
      }
    })

export type UserDetailsFormValues = z.infer<ReturnType<typeof UserDetailsFormSchema>>

function normalizePhoneForCompare(v: string | null | undefined): string {
  if (v == null || v === '') return ''
  return String(v).replace(/\s/g, '')
}

function birthDateToTime(v: UserDetailsFormValues['birthDate']): number | null {
  if (v == null) return null
  if (v instanceof Date) return v.getTime()
  const d = new Date(v as string)
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

function birthDateToYear(v: UserDetailsFormValues['birthDate']): string | null {
  const time = birthDateToTime(v)
  return time == null ? null : String(new Date(time).getFullYear())
}

function formValuesEqual(current: UserDetailsFormValues, initial: UserDetailsFormValues): boolean {
  if (current.name !== initial.name) return false
  if (normalizePhoneForCompare(current.phoneNumber) !== normalizePhoneForCompare(initial.phoneNumber)) return false
  if ((current.nickname ?? null) !== (initial.nickname ?? null)) return false
  if ((current.jobTitle ?? null) !== (initial.jobTitle ?? null)) return false
  if ((current.gender ?? null) !== (initial.gender ?? null)) return false
  if (current.image !== initial.image) return false
  if (current.imageFile instanceof File) return false
  if (birthDateToTime(current.birthDate) !== birthDateToTime(initial.birthDate)) return false
  return true
}

type ImageSelectorMode = 'upload' | 'avatar'

interface UserDetailsFormProps {
  onSubmit: (values: UserDetailsFormValues) => Promise<void> | void
  initialValues?: Partial<UserDetailsFormValues>
  email?: string
  phoneNumber?: string
  submitButtonText?: string
  submitButtonClassName?: string
  /**
   * Extra actions rendered to the left of the submit button in a right-aligned footer row.
   * When provided the submit button shrinks to fit instead of spanning the full width.
   */
  footerActions?: ReactNode
  /** Mode for image selection: 'upload' for file upload, 'avatar' for avatar selector (default: 'upload') */
  imageSelectorMode?: ImageSelectorMode
  /** Avatar data required when imageSelectorMode is 'avatar' */
  avatarData?: AvatarList[]
  /** Placeholder fallback for avatar selector */
  avatarPlaceholder?: string
  /** When true, phone number is editable in the form (no OTP). When false, phone is disabled with a link to change it (default: false) */
  editablePhoneNumber?: boolean
  /** When true, show optional nickname field (Panggilan akrab) */
  showNickname?: boolean
  /** Placeholder for the nickname field. Copy differs per app (school vs family). */
  nicknamePlaceholder?: string
  /** Helper text rendered under the nickname field. Copy differs per app (school vs family). */
  nicknameDescription?: string
  /** When true, show optional job title select (Jabatan). Typically used when organizationType is 'school'. */
  showJobTitle?: boolean
  /** Options for job title select. Used when showJobTitle is true. */
  jobTitleOptions?: readonly { label: string; value: string }[]
  /** When true, show optional gender select (Jenis kelamin) */
  showGender?: boolean
  /** When true, show optional birth date picker (Tanggal lahir) */
  showBirthDate?: boolean
  /** When true, show optional email field (Email) */
  showEmail?: boolean
  /** When true, show optional phone number field (Nomor telepon) */
  showPhoneNumber?: boolean
  className?: string
  locale?: Locale
}

export function UserDetailsForm({
  onSubmit,
  initialValues = {},
  email,
  phoneNumber,
  submitButtonText: submitButtonTextProp,
  submitButtonClassName,
  footerActions,
  imageSelectorMode = 'upload',
  avatarData = [],
  avatarPlaceholder = '?',
  editablePhoneNumber = false,
  showNickname = false,
  nicknamePlaceholder,
  nicknameDescription,
  showJobTitle = false,
  showEmail = true,
  showPhoneNumber = true,
  jobTitleOptions: jobTitleOptionsProp,
  showGender = false,
  showBirthDate = false,
  className,
  locale = 'id',
}: UserDetailsFormProps) {
  const jobTitleOptions = jobTitleOptionsProp ?? DEFAULT_JOB_TITLE_OPTIONS(locale)

  const birthYearOptions = useMemo(() => buildBirthYearOptions(new Date().getFullYear()), [])

  const submitButtonText = submitButtonTextProp ?? (locale === 'id' ? 'Perbarui' : 'Update')

  const hasPhoneNumber = Boolean(phoneNumber?.trim())

  const initialFormValues = useMemo(
    (): UserDetailsFormValues => ({
      name: initialValues.name ?? '',
      phoneNumber: initialValues.phoneNumber ? initialValues.phoneNumber.replace(/\s/g, '') : null,
      imageFile: initialValues.imageFile ?? null,
      image: initialValues.image ?? null,
      nickname: initialValues.nickname ?? null,
      jobTitle: initialValues.jobTitle ?? null,
      gender: initialValues.gender ?? null,
      birthDate: initialValues.birthDate
        ? typeof initialValues.birthDate === 'string'
          ? new Date(initialValues.birthDate)
          : initialValues.birthDate
        : null,
    }),
    [
      initialValues.name,
      initialValues.phoneNumber,
      initialValues.imageFile,
      initialValues.image,
      initialValues.nickname,
      initialValues.jobTitle,
      initialValues.gender,
      initialValues.birthDate,
    ],
  )

  const form = useForm({
    defaultValues: initialFormValues as z.input<ReturnType<typeof UserDetailsFormSchema>>,
    validators: {
      onSubmit: UserDetailsFormSchema(locale),
    },
    onSubmit: async ({ value, formApi }) => {
      const [_, error] = await tryCatch(async () => await onSubmit(value))

      if (error) {
        // @ts-expect-error - error.message is a string
        formApi.setErrorMap({ onSubmit: error.message })
      }
    },
  })

  useEffect(() => {
    void form.validateAllFields('change')
  }, [])

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const canSubmit = useStore(form.store, (state) => state.canSubmit)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const values = useStore(form.store, (state) => state.values) as UserDetailsFormValues
  const hasChanges = !formValuesEqual(values, initialFormValues)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
      className={className}
    >
      <FieldSet>
        <div className='srd:flex srd:w-full srd:flex-col srd:gap-y-4'>
          <div className='srd:flex srd:w-full srd:items-center srd:justify-center'>
            {imageSelectorMode === 'upload' ? (
              <form.Field name='imageFile'>
                {(field) => (
                  <ProfileUpload
                    handleImageUploadAction={(file) => {
                      field.handleChange(file)
                    }}
                    placeholderName={values.name ?? 'Nama'}
                    initialImageUrl={values.image}
                    locale={locale}
                    size={60}
                  />
                )}
              </form.Field>
            ) : (
              <form.Field name='image'>
                {(field) => (
                  <AvatarSelector
                    handleAvatarSelectAction={(avatar) => field.handleChange(avatar.src)}
                    avatarData={avatarData}
                    initialAvatarKey={
                      field.state.value
                        ? (avatarData.find((a) => a.src === field.state.value)?.key ?? avatarData[0]?.key ?? null)
                        : null
                    }
                    placeholderFallback={avatarPlaceholder}
                    size={80}
                  />
                )}
              </form.Field>
            )}
          </div>

          <form.Field name='name'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

              return (
                <Field className='srd:gap-1' data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Nama lengkap' : 'Full name'}</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          {showNickname && (
            <form.Field name='nickname'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                return (
                  <Field className='srd:gap-1 srd:w-full' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Panggilan akrab' : 'Nickname'}</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value ?? ''}
                      placeholder={
                        nicknamePlaceholder ??
                        (locale === 'id' ? 'Cth: Pak Budi, Miss Devi, dsb.' : 'E.g. Mr. Budi, Miss Devi, etc.')
                      }
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value || null)}
                      aria-invalid={isInvalid}
                    />
                    {nicknameDescription && <FieldDescription>{nicknameDescription}</FieldDescription>}
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>
          )}

          {showJobTitle && (
            <form.Field name='jobTitle'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                const options = [...jobTitleOptions]

                return (
                  <Field className='srd:gap-1' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Jabatan' : 'Job Title'}</FieldLabel>
                    <Select
                      name={field.name}
                      value={field.state.value}
                      onValueChange={(value) => field.handleChange(value)}
                      items={options}
                    >
                      <SelectTrigger id={field.name} aria-invalid={isInvalid} className='srd:w-full'>
                        <SelectValue
                          placeholder={locale === 'id' ? 'Jabatan' : 'Job title'}
                          className={cn(!field.state.value && 'srd:text-grey-100')}
                        />
                      </SelectTrigger>
                      <SelectContent align='start'>
                        {options.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>
          )}

          {email !== undefined && showEmail && (
            <Field className='srd:gap-1'>
              <FieldLabel htmlFor='email'>Email</FieldLabel>
              <Input id='email' name='email' value={email} disabled />
              <Button
                className={cn(
                  'srd:text-blue-100 srd:-mt-2.5 srd:w-fit srd:p-0',
                  typographyVariants({ variant: 'label-sm' }),
                )}
                asChild
                variant='link'
                size='sm'
              >
                <a href={buildUrl('account', '/ganti-email').href} target='_blank'>
                  {locale === 'id' ? 'Ganti email' : 'Change email'}{' '}
                  <ExternalLink className='srd:ml-1 srd:inline-block srd:size-3' />
                </a>
              </Button>
            </Field>
          )}

          {showPhoneNumber ? (
            editablePhoneNumber ? (
              <form.Field name='phoneNumber'>
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                  return (
                    <Field className='srd:gap-1' data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Ponsel' : 'Mobile number'}</FieldLabel>
                      <PhoneInput
                        id={field.name}
                        name={field.name}
                        value={field.state.value ?? undefined}
                        onBlur={field.handleBlur}
                        onChange={(value) => field.handleChange(value)}
                        aria-invalid={isInvalid}
                      />
                      {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    </Field>
                  )
                }}
              </form.Field>
            ) : (
              <Field className='srd:gap-1'>
                <FieldLabel htmlFor='phoneNumber'>{locale === 'id' ? 'Ponsel' : 'Mobile number'}</FieldLabel>
                <PhoneInput id='phoneNumber' name='phoneNumber' value={phoneNumber} disabled />
                <Button
                  className={cn(
                    'srd:text-blue-100 srd:-mt-2.5 srd:w-fit srd:p-0',
                    typographyVariants({ variant: 'label-sm' }),
                  )}
                  asChild
                  variant='link'
                  size='sm'
                >
                  <a href={buildUrl('account', '/ganti-nomor-ponsel').href} target='_blank'>
                    {hasPhoneNumber
                      ? locale === 'id'
                        ? 'Ganti nomor ponsel'
                        : 'Change phone number'
                      : locale === 'id'
                        ? 'Tambah nomor ponsel'
                        : 'Add phone number'}{' '}
                    <ExternalLink className='srd:ml-1 srd:inline-block srd:size-3' />
                  </a>
                </Button>
              </Field>
            )
          ) : null}

          {showGender && (
            <form.Field name='gender'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                return (
                  <Field className='srd:gap-1 srd:w-full' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Jenis kelamin' : 'Gender'}</FieldLabel>
                    <Select
                      name={field.name}
                      value={field.state.value}
                      onValueChange={(value) => field.handleChange(value)}
                      items={GENDER_OPTIONS(locale)}
                    >
                      <SelectTrigger id={field.name} aria-invalid={isInvalid} className='srd:w-full'>
                        <SelectValue
                          placeholder={locale === 'id' ? 'Jenis kelamin' : 'Gender'}
                          className={cn(!field.state.value && 'srd:text-grey-100')}
                        />
                      </SelectTrigger>
                      <SelectContent align='start'>
                        {GENDER_OPTIONS(locale).map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>
          )}

          {showBirthDate && (
            <form.Field name='birthDate'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                const selectedYear = birthDateToYear(field.state.value)

                return (
                  <Field className='srd:gap-1 srd:w-full' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Tahun lahir' : 'Birth year'}</FieldLabel>
                    <Select
                      name={field.name}
                      value={selectedYear}
                      onValueChange={(value) => field.handleChange(value ? new Date(Number(value), 0, 1) : null)}
                      items={birthYearOptions}
                    >
                      <SelectTrigger id={field.name} aria-invalid={isInvalid} className='srd:w-full'>
                        <SelectValue
                          placeholder={locale === 'id' ? 'Tahun lahir' : 'Birth year'}
                          className={cn(!selectedYear && 'srd:text-grey-100')}
                        />
                      </SelectTrigger>
                      <SelectContent align='start'>
                        {birthYearOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>
          )}
        </div>
      </FieldSet>

      {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' ? (
        <p className='srd:text-error srd:mt-4 srd:text-xs'>{errorMap.onSubmit}</p>
      ) : null}

      <div
        className={cn('srd:mt-8', footerActions && 'srd:flex srd:flex-wrap srd:items-center srd:justify-end srd:gap-3')}
      >
        {footerActions}

        <Button
          type='submit'
          disabled={!canSubmit || !hasChanges}
          loading={Boolean(isSubmitting)}
          className={cn(
            'srd:bg-blue-100 srd:rounded-full',
            footerActions ? 'srd:w-fit' : 'srd:w-full',
            submitButtonClassName,
          )}
        >
          {submitButtonText}
        </Button>
      </div>
    </form>
  )
}
